import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchX, Lightbulb, PackageSearch, Star } from 'lucide-react';
import { Button } from './ui/button';
import { productsAPI } from '../services/api';
import { productPath } from '../services/productPath';
import { prodName } from '../services/catalogI18n';
import { useT } from '../services/i18n';
import PartRequestDialog from './PartRequestDialog';

/** Search page empty state: closest references (typos / partial refs) + « ask for this part » form. */
const SearchNoResults = ({ query, vehicle, children }) => {
  const { t, lang } = useT();
  const [near, setNear] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setNear(null);
    if (!query) return undefined;
    productsAPI.near(query).then((d) => { if (!cancelled) setNear(d.items || []); }).catch(() => { if (!cancelled) setNear([]); });
    return () => { cancelled = true; };
  }, [query]);
  return (
    <div className="space-y-6" data-testid="search-empty">
      <div className="bg-white rounded-lg shadow-sm p-10 text-center">
        <SearchX size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-600">{query ? t('search.noMatch', { q: query }) : t('search.hint')}</p>
        {children}
        {query && (
          <div className="mt-5 flex flex-col items-center gap-2">
            <Button onClick={() => setAskOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="search-ask-part">
              <PackageSearch size={16} className="mr-2" /> {t('partRequest.cta')}
            </Button>
            <p className="text-xs text-gray-400 max-w-md">{t('partRequest.ctaHint')}</p>
          </div>
        )}
      </div>
      {near && near.length > 0 && (
        <section className="bg-white rounded-lg shadow-sm p-5" data-testid="search-near-matches">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-3"><Lightbulb size={18} className="text-yellow-500" /> {t('search.nearTitle')}</h2>
          <ul className="divide-y divide-gray-100">
            {near.map((p) => (
              <li key={p.id}>
                <Link to={productPath(p)} className="flex items-center gap-3 py-2.5 hover:bg-orange-50 rounded-md px-2 -mx-2" data-testid={`search-near-${p.id}`}>
                  <img src={p.image} alt="" className="w-12 h-12 object-contain bg-gray-50 rounded border border-gray-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{prodName(p, lang)}</p>
                    <p className="text-xs text-gray-500 truncate">{p.brand} · {t('common.ref')} {p.ref}{p.match_ref && p.match_ref !== p.ref ? ` · ${t('search.nearVia')} ${p.match_ref}` : ''}</p>
                  </div>
                  {Number(p.reviews) > 0 && <span className="text-[11px] text-gray-600 inline-flex items-center gap-0.5 whitespace-nowrap"><Star size={10} className="fill-yellow-400 text-yellow-400" /> {Number(p.rating).toFixed(1)}</span>}
                  <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{Number(p.price).toFixed(2)} €</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <PartRequestDialog open={askOpen} onOpenChange={setAskOpen} query={query} vehicle={vehicle} />
    </div>
  );
};

export default SearchNoResults;
